import type { PageAgentViewer } from '../hooks/usePageAgentViewers.ts';

import type {
	AgentContributorAttribution,
	PageAgentContributor,
} from './normalizePageAgentContributors.ts';
import type { PagePerson, PagePersonRelationshipEvidence } from './parsePagePeople.ts';
import type { RankedPagePerson } from './pagePeople.ts';
import { PAGE_PEOPLE_RELATIONSHIPS } from './pagePeopleRelationships.ts';

/** One relationship and attributed user per agent; contribution takes precedence over viewing. */
type SectionAgent = Pick<
	PageAgentViewer,
	'id' | 'identityAccountId' | 'name' | 'avatarURL' | 'agentIcon' | 'lastViewedAt'
> & {
	attributedUser?: AgentContributorAttribution;
	relationship: 'contributed' | 'viewed';
};

export type RankedPagePersonOrAgent = RankedPagePerson & { agent?: SectionAgent };

type PagePersonCandidate = {
	person: PagePerson;
	agent: SectionAgent | undefined;
	primary: PagePersonRelationshipEvidence | undefined;
};

const EXCLUDED_RELATIONSHIP_PRIORITY = 5;

const relationshipOrder = new Map(
	PAGE_PEOPLE_RELATIONSHIPS.map(({ relationship }, index) => [relationship, index]),
);

const priority = (
	relationship: PagePersonRelationshipEvidence['relationship'],
	isAgent: boolean,
): number => {
	switch (relationship) {
		case 'owns':
			return 0;
		case 'created':
		case 'contributed':
		case 'updated':
			return isAgent ? 3 : 1;
		case 'viewed':
			return isAgent ? 4 : EXCLUDED_RELATIONSHIP_PRIORITY;
		case 'mentioned-in':
			return 2;
		default:
			return EXCLUDED_RELATIONSHIP_PRIORITY;
	}
};

// Graph timestamps are relationship recency evidence, not necessarily individual action times.
const timestamp = (value: number | null): number =>
	value != null && Number.isFinite(value) ? value : -Infinity;

const compareText = (left: string, right: string): number =>
	left < right ? -1 : left > right ? 1 : 0;

const compareRelationships = (
	left: PagePersonRelationshipEvidence,
	right: PagePersonRelationshipEvidence,
	isAgent: boolean,
): number =>
	priority(left.relationship, isAgent) - priority(right.relationship, isAgent) ||
	timestamp(right.lastUpdated) - timestamp(left.lastUpdated) ||
	(relationshipOrder.get(left.relationship) ?? Number.MAX_SAFE_INTEGER) -
		(relationshipOrder.get(right.relationship) ?? Number.MAX_SAFE_INTEGER);

const pickPrimaryRelationship = (
	person: PagePerson,
	isAgent: boolean,
): PagePersonRelationshipEvidence | undefined => {
	let primary: PagePersonRelationshipEvidence | undefined;
	for (const relationship of person.relationships) {
		if (!primary || compareRelationships(relationship, primary, isAgent) < 0) {
			primary = relationship;
		}
	}
	return primary;
};

const lastViewedTimestamp = (agent: Pick<PageAgentViewer, 'lastViewedAt'>): number | null => {
	const timestamp = agent.lastViewedAt ? Date.parse(agent.lastViewedAt) : NaN;
	return Number.isFinite(timestamp) ? timestamp : null;
};

/** Rank all candidates by role, then recency. Each surface selects from this shared order. */
export const rankPagePeopleAndAgents = (
	people: readonly PagePerson[],
	agents: readonly PageAgentViewer[],
	contributors: readonly PageAgentContributor[] = [],
): RankedPagePersonOrAgent[] => {
	const agentsByIdentity = new Map<string, SectionAgent>();
	for (const contributor of contributors) {
		// Without contribution timestamps, keep the first returned entry for each agent.
		if (!agentsByIdentity.has(contributor.accountId)) {
			agentsByIdentity.set(contributor.accountId, {
				id: contributor.accountId,
				identityAccountId: contributor.accountId,
				name: contributor.name,
				avatarURL: contributor.avatarURL,
				attributedUser: contributor.attributedUsers[0],
				relationship: 'contributed',
			});
		}
	}
	for (const agent of agents) {
		// Analytics IDs can include an attributed user's ID; the identity is the agent itself.
		// Unresolved agents retain their source row ID rather than guessing from a display name.
		const identity = agent.identityAccountId || agent.id;
		const existing = agentsByIdentity.get(identity);
		if (existing?.relationship === 'contributed') {
			continue;
		}
		if (
			!existing ||
			(lastViewedTimestamp(agent) ?? -Infinity) > (lastViewedTimestamp(existing) ?? -Infinity)
		) {
			agentsByIdentity.set(identity, {
				id: agent.id,
				identityAccountId: agent.identityAccountId,
				name: agent.name,
				avatarURL: agent.avatarURL || existing?.avatarURL || '',
				agentIcon: agent.agentIcon,
				lastViewedAt: agent.lastViewedAt,
				attributedUser: agent.attributedUsers?.find(({ name }) => name.trim()),
				relationship: 'viewed',
			});
		}
	}

	const agentsByKey = new Map<string, SectionAgent>();
	const agentCandidates: PagePerson[] = [];
	for (const [identity, agent] of agentsByIdentity) {
		// A local row key, never a graph ARI or a navigation target.
		const key = `agent:${identity}`;
		agentsByKey.set(key, agent);
		agentCandidates.push({
			ari: key,
			accountId: agent.identityAccountId ?? '',
			name: agent.name,
			picture: agent.avatarURL || null,
			relationships: [
				{
					relationship: agent.relationship,
					direction: 'incoming',
					lastUpdated: agent.relationship === 'viewed' ? lastViewedTimestamp(agent) : null,
				},
			],
		});
	}

	const candidates: PagePersonCandidate[] = [...people, ...agentCandidates].map((person) => {
		const agent = agentsByKey.get(person.ari);
		return { person, agent, primary: pickPrimaryRelationship(person, !!agent) };
	});
	const eligiblePeople = candidates.filter(
		(candidate): candidate is PagePersonCandidate & { primary: PagePersonRelationshipEvidence } =>
			!!candidate.primary &&
			priority(candidate.primary.relationship, !!candidate.agent) < EXCLUDED_RELATIONSHIP_PRIORITY,
	);

	return eligiblePeople
		.sort(
			(left, right) =>
				priority(left.primary.relationship, !!left.agent) -
					priority(right.primary.relationship, !!right.agent) ||
				timestamp(right.primary.lastUpdated) - timestamp(left.primary.lastUpdated) ||
				compareText(left.person.ari, right.person.ari) ||
				compareText(left.person.accountId, right.person.accountId),
		)
		.map(({ person, primary, agent }) => ({
			ari: person.ari,
			accountId: person.accountId,
			name: person.name,
			picture: person.picture,
			...(person.jobTitle ? { jobTitle: person.jobTitle } : {}),
			primaryRelationship: primary.relationship,
			relationshipDirection: primary.direction,
			...(agent ? { agent } : {}),
		}));
};
